import type { CustomerReceivable, Receivables, VendorPayable } from '../api/outstanding';
import { formatAmount } from '../format/money';

import { ageingBands, dueWords } from './ageing';

/**
 * The words around Outstanding's two sides (Option A of the design canvas, 2026-10-22): the figure
 * over 60 days, each vendor's line, and the reminder a clerk can send. Every amount is the server's;
 * this only adds up what the list already holds.
 */

const rupees = (value: number) => `₹${formatAmount(value)}`;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** What has been owed past 60 days, and by how many customers — the second figure across the top. */
export function overSixty(receivables: Pick<Receivables, 'ageing' | 'customers'>): {
  amount: number;
  customers: number;
} {
  return {
    amount: receivables.ageing.upTo90 + receivables.ageing.over90,
    customers: receivables.customers.filter((c) => c.ageing.upTo90 + c.ageing.over90 > 0).length,
  };
}

/**
 * A vendor's line under its name: what is owed on, then where the earliest stands — "1 bill · due in
 * 3 days", "12 jobs · 2 days late", "2 bills, 1 job · due today". Money held on account says so.
 */
export function vendorOwesWords(
  vendor: Pick<VendorPayable, 'items' | 'onAccount' | 'uncostedJobs'>,
): string {
  const bills = vendor.items.filter((item) => item.kind === 'Bill').length;
  const jobs = vendor.items.filter((item) => item.kind === 'Job').length;
  const what = [bills ? plural(bills, 'bill') : null, jobs ? plural(jobs, 'job') : null]
    .filter(Boolean)
    .join(', ');
  const first = vendor.items[0];

  const parts = [
    what || null,
    first ? dueWords(first.daysPastDue) : null,
    vendor.onAccount > 0 ? `${rupees(vendor.onAccount)} on account` : null,
    vendor.uncostedJobs > 0 ? `${plural(vendor.uncostedJobs, 'job')} with no cost yet` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'Nothing owed';
}

/**
 * The reminder a clerk sends on WhatsApp (owner, 2026-10-22): the customer's first name, what is
 * owed and on what, and the shop. The app opens WhatsApp with it typed in and records nothing.
 */
export function reminderText(
  customer: Pick<CustomerReceivable, 'name' | 'total' | 'items'>,
  shop: { name: string; phone?: string | null | undefined },
): string {
  const first = customer.name.trim().split(/\s+/)[0] ?? customer.name;
  const on = customer.items.map((item) => item.reference).join(', ');

  return [
    `Namaste ${first} ji, ${rupees(customer.total)} is due${on ? ` on ${on}` : ''} at ${shop.name}.`,
    shop.phone ? `— ${shop.phone}` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/** A statement's content, before the shop's letterhead — what both the website and the phone print. */
export interface Statement {
  asOn: string;
  party: { name: string; mobile: string | null };
  dateLabel: string;
  lines: { reference: string; what: string; date: string; age: string; amount: number }[];
  bands: { label: string; amount: number }[];
  total: number;
  onAccount?: number | undefined;
  closing?: string | undefined;
}

/** A customer's statement: each booking or sale owed on, since when, and the age bands. */
export function customerStatement(customer: CustomerReceivable, asOn: string): Statement {
  return {
    asOn,
    party: { name: customer.name, mobile: customer.mobile || null },
    dateLabel: 'Since',
    lines: customer.items.map((item) => ({
      reference: item.reference,
      what: item.kind === 'Sale' ? 'Sale' : 'Booking',
      date: item.since,
      age: item.days <= 0 ? 'today' : plural(item.days, 'day'),
      amount: item.outstanding,
    })),
    bands: ageingBands(customer.ageing, 'receive').map((band) => ({
      label: band.label,
      amount: band.amount,
    })),
    total: customer.total,
    closing: 'Please pay at the counter or by UPI to the shop. Ignore this if it is already paid.',
  };
}

/** A vendor's statement: each bill or job owed, when it fell or falls due, and money on account. */
export function vendorStatement(vendor: VendorPayable, asOn: string): Statement {
  return {
    asOn,
    party: { name: vendor.name, mobile: vendor.mobile },
    dateLabel: 'Due',
    lines: vendor.items.map((item) => ({
      reference: item.reference,
      what: item.description,
      date: item.dueDate,
      age: dueWords(item.daysPastDue),
      amount: item.outstanding,
    })),
    bands: ageingBands(vendor.ageing, 'pay').map((band) => ({
      label: band.label,
      amount: band.amount,
    })),
    total: vendor.owed,
    onAccount: vendor.onAccount > 0 ? vendor.onAccount : undefined,
  };
}
